/** Пять вкладок и состояние фонового монтажа. */
import type { ReactElement } from 'react';
import * as telegram from '../lib/telegram';
import { t } from '../i18n';

export type Tab = 'invite' | 'projects' | 'new' | 'balance' | 'profile';

export interface TabBarProps {
  active: Tab;
  onSelect: (tab: Tab) => void;
  processing?: boolean;
  readyCount?: number;
}

const ICON_PROPS = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
} as const;

function InviteIcon(): ReactElement {
  return (
    <svg {...ICON_PROPS}>
      <circle cx="10" cy="8" r="4" />
      <path d="M3 21c0-4 3-7 7-7 1.3 0 2.5.3 3.5.9M19 14v6M16 17h6" />
    </svg>
  );
}

function ProjectsIcon(): ReactElement {
  return (
    <svg {...ICON_PROPS}>
      <rect x="3" y="5" width="18" height="14" rx="2.5" />
      <path d="M7 5v14M17 5v14M3 10h4M3 14h4M17 10h4M17 14h4" />
    </svg>
  );
}

function MontageIcon(): ReactElement {
  return (
    <svg {...ICON_PROPS}>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M3 10h18M7 5l3 5M12 5l3 5M17 5l3 5" />
    </svg>
  );
}

function BalanceIcon(): ReactElement {
  return (
    <svg {...ICON_PROPS}>
      <ellipse cx="12" cy="6" rx="7" ry="3" />
      <path d="M5 6v6c0 1.7 3.1 3 7 3s7-1.3 7-3V6M5 12v6c0 1.7 3.1 3 7 3s7-1.3 7-3v-6" />
    </svg>
  );
}

function ProfileIcon(): ReactElement {
  return (
    <svg {...ICON_PROPS}>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21c0-4.4 3.6-7 8-7s8 2.6 8 7" />
    </svg>
  );
}

const TABS: ReadonlyArray<{ id: Tab; labelKey: 'components.tabs.new' | 'components.tabs.projects' | 'components.tabs.balance' | 'components.tabs.invite' | 'components.tabs.profile'; Icon: () => ReactElement }> = [
  { id: 'new', labelKey: 'components.tabs.new', Icon: MontageIcon },
  { id: 'projects', labelKey: 'components.tabs.projects', Icon: ProjectsIcon },
  { id: 'balance', labelKey: 'components.tabs.balance', Icon: BalanceIcon },
  { id: 'invite', labelKey: 'components.tabs.invite', Icon: InviteIcon },
  { id: 'profile', labelKey: 'components.tabs.profile', Icon: ProfileIcon },
];

export function TabBar({ active, onSelect, processing = false, readyCount = 0 }: TabBarProps): ReactElement {
  return (
    <nav className="tabbar" aria-label={t('components.tabs.aria')}>
      {TABS.map(({ id, labelKey, Icon }) => {
        const label = t(labelKey);
        const isActive = id === active;
        return (
          <button
            key={id}
            type="button"
            className={`tab${isActive ? ' active' : ''}`}
            aria-label={label}
            aria-current={isActive ? 'page' : undefined}
            onClick={() => {
              telegram.hapticFeedback.selection();
              onSelect(id);
            }}
          >
            <span className="tab-icon">
              <Icon />
              {id === 'projects' && processing ? <span className="tab-processing" role="status" aria-label={t('components.tabs.processing')} /> : null}
              {id === 'projects' && readyCount > 0 ? <span className="tab-badge" aria-label={t('components.tabs.ready', { n: readyCount })}>{readyCount}</span> : null}
            </span>
            <span className="tab-label">{label}</span>
          </button>
        );
      })}
    </nav>
  );
}
